import { useState, useMemo } from 'react';
import { TrendingUp, Info } from 'lucide-react';
import { useProjectExchangeRate } from '../../../../hooks/useProjectExchangeRate';
import { useCurrentProject } from '../../../../hooks/useProjectData';
import { formatNumber, formatCurrency } from '../../utils/analysis.utils';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import type { ChartOptions } from 'chart.js';

import {
  EnergySavingsMaximumCard,
  PeakShavingMaximumCard,
  DataCoverageInfo,
  ValueCaptureCurve,
  PriceDistribution,
  safeNumber,
  calculatePriceStatistics,
  calculateConsumptionStatistics,
  calculateConfidence
} from './theoretical-maximum';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  Filler,
  annotationPlugin
);

interface TheoreticalMaximumSectionProps {
  prices?: any;
  consumption?: any;
  bill?: any;
  configuration?: any;
  exchangeRate?: number;
}

export function TheoreticalMaximumSection({
  prices,
  consumption,
  bill,
  configuration,
  exchangeRate: providedExchangeRate
}: TheoreticalMaximumSectionProps) {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  // Use the exchange rate from the Bill (Step 1) saved per project
  const { data: currentProject } = useCurrentProject();
  const { data: exchangeRateData } = useProjectExchangeRate(currentProject?.id);
  const exchangeRate = providedExchangeRate || exchangeRateData?.project_rate || 900;

  const analysis = useMemo(() => {
    if (!prices?.data || !consumption?.data) return null;

    const retailTariff = bill?.energyRate
      ? (bill.energyRate / exchangeRate * 1000)
      : 70;

    const valueOpportunities = prices.data.map((p: any, index: number) => {
      const spotPrice = safeNumber(p.priceUsd, 0);
      const consumptionForHour = consumption.data[index]?.consumption || 0;
      return {
        timestamp: p.timestamp,
        spotPrice: spotPrice,
        valuePerMWh: Math.max(0, retailTariff - spotPrice),
        actualConsumption: consumptionForHour,
        hour: new Date(p.timestamp).getHours(),
        dayOfWeek: new Date(p.timestamp).getDay()
      };
    });

    const sortedOpportunities = [...valueOpportunities].sort((a, b) => a.spotPrice - b.spotPrice);

    const totalConsumption = consumption.data.reduce((sum: number, c: any) => {
      const consumptionKwh = safeNumber(c.consumption, 0);
      return sum + consumptionKwh;
    }, 0);

    const averageConsumption = totalConsumption / Math.max(1, consumption.data.length);
    const maxConsumption = Math.max(...consumption.data.map((c: any) => safeNumber(c.consumption, 0)));
    const gridCapacityKw = configuration?.grid?.maxConnectionKw || (maxConsumption * 2);
    const maxHourlyCapacity = gridCapacityKw;
    const avgHourlyConsumption = maxHourlyCapacity;

    let cumulativeConsumption = 0;
    let hoursUsed = 0;
    let theoreticalCost = 0;
    let cutoffPrice = 0;

    for (const opportunity of sortedOpportunities) {
      if (cumulativeConsumption >= totalConsumption) break;

      const consumptionThisHour = Math.min(avgHourlyConsumption, totalConsumption - cumulativeConsumption);

      cumulativeConsumption += consumptionThisHour;
      theoreticalCost += (consumptionThisHour / 1000) * opportunity.spotPrice;
      hoursUsed++;
      cutoffPrice = opportunity.spotPrice;

      if (cumulativeConsumption >= totalConsumption) break;
    }

    const baselineCostRetail = (totalConsumption / 1000) * retailTariff;

    const savings = baselineCostRetail - theoreticalCost;

    const valueCaptureCurve: any[] = [];
    let cumConsumption = 0;
    let cumSavings = 0;

    for (let i = 0; i < sortedOpportunities.length; i++) {
      const opportunity = sortedOpportunities[i];
      const consumptionThisHour = Math.min(avgHourlyConsumption, totalConsumption - cumConsumption);

      if (cumConsumption >= totalConsumption) break;

      cumConsumption += consumptionThisHour;
      const costThisHour = (consumptionThisHour / 1000) * opportunity.spotPrice;
      const baselineCostThisHour = (consumptionThisHour / 1000) * retailTariff;
      cumSavings += (baselineCostThisHour - costThisHour);

      if (i % 100 === 0 || i === hoursUsed - 1) {
        valueCaptureCurve.push({
          hours: i + 1,
          savings: cumSavings,
          percentOfTotal: (cumSavings / savings) * 100
        });
      }
    }

    const hoursBelowRetail = valueOpportunities.filter(o => o.spotPrice < retailTariff).length;
    const percentBelowRetail = (hoursBelowRetail / valueOpportunities.length) * 100;
    const hoursInYear = valueOpportunities.length;
    const percentOfYear = (hoursUsed / hoursInYear) * 100;

    // Peak Shaving calculations - always use demandHpKw from bill
    const peakDemandKw = bill?.demandHpKw || 0;
    const demandHpRate = bill?.demandHpRate || 0;
    // Calculate charge from kW and rate (don't use demandHpCharge directly)
    const demandHpCharge = peakDemandKw * demandHpRate;
    const monthlySavings = demandHpCharge;
    const annualSavings = monthlySavings * 12;

    // Access generator config - prioritize flat structure from Zustand store
    const generatorCostPerHour = configuration?.generatorCostPerHour || configuration?.generator?.costPerHour || 0;
    const generatorEnabled = configuration?.generatorEnabled || configuration?.generator?.useGenerator || false;
    const generatorHoursPerDay = configuration?.generatorHoursPerDay || configuration?.generator?.hoursPerDay || 4;
    const weekdaysPerYear = 260;
    const generatorDailyCost = generatorCostPerHour * generatorHoursPerDay;
    const generatorAnnualCost = generatorDailyCost * weekdaysPerYear;

    const billEnergyCharge = bill?.energyCharge || 0;
    const billEnergyChargeUsd = billEnergyCharge / exchangeRate;
    const totalAnnualEnergyChargeBill = billEnergyChargeUsd * 12;
    const totalAnnualEnergyChargeCalc = baselineCostRetail;
    const totalAnnualEnergyCharge = totalAnnualEnergyChargeCalc || totalAnnualEnergyChargeBill || baselineCostRetail;

    const priceStats = calculatePriceStatistics(valueOpportunities);
    const consumptionStats = calculateConsumptionStatistics(consumption.data);
    const confidence = calculateConfidence(priceStats, consumptionStats);

    return {
      theoretical: {
        maximum: savings,
        baselineCost: baselineCostRetail,
        theoreticalCost,
        hoursNeeded: hoursUsed,
        percentOfYear,
        avgValuePerMWh: savings / (totalConsumption / 1000),
        cutoffPrice
      },
      peakShaving: {
        demandHpKw: peakDemandKw,
        demandHpRate,
        demandHpCharge,
        monthlySavingsUsd: monthlySavings / exchangeRate,
        annualSavingsUsd: annualSavings / exchangeRate,
        combinedSavings: savings + (annualSavings / exchangeRate)
      },
      generatorSavings: generatorEnabled ? {
        enabled: true,
        costPerHour: generatorCostPerHour,
        hoursPerDay: generatorHoursPerDay,
        weekdaysPerYear,
        dailyCost: generatorDailyCost,
        monthlyCost: generatorDailyCost * (weekdaysPerYear / 12),
        annualCost: generatorAnnualCost
      } : { enabled: false },
      totalConsumption,
      maxHourlyConsumption: maxConsumption,
      gridCapacityKw,
      retailTariff,
      valueOpportunities,
      sortedOpportunities,
      hoursBelowRetail,
      percentBelowRetail,
      hoursInYear,
      valueCaptureCurve,
      totalAnnualEnergyCharge,
      totalAnnualEnergyChargeBill,
      totalAnnualEnergyChargeCalc,
      priceStats,
      consumptionStats,
      confidence
    };
  }, [prices, consumption, bill, configuration, exchangeRate]);

  const retailTariffValue = analysis?.retailTariff || 70;

  const priceDistribution = useMemo(() => {
    if (!prices?.data) return [];

    const bins = [
      { range: '0', min: 0, max: 0.1, count: 0, color: 'bg-gray-400', textColor: 'text-gray-600' },
      { range: '0-10', min: 0.1, max: 10, count: 0, color: 'bg-gray-500', textColor: 'text-gray-600' },
      { range: '10-20', min: 10, max: 20, count: 0, color: 'bg-gray-500', textColor: 'text-gray-600' },
      { range: '20-30', min: 20, max: 30, count: 0, color: 'bg-gray-500', textColor: 'text-gray-600' },
      { range: '30-40', min: 30, max: 40, count: 0, color: 'bg-gray-600', textColor: 'text-gray-600' },
      { range: '40-50', min: 40, max: 50, count: 0, color: 'bg-gray-600', textColor: 'text-gray-600' },
      { range: '50-60', min: 50, max: 60, count: 0, color: 'bg-gray-600', textColor: 'text-gray-600' },
      { range: '60-70', min: 60, max: 70, count: 0, color: 'bg-gray-700', textColor: 'text-gray-600' },
      { range: '70-80', min: 70, max: 80, count: 0, color: 'bg-gray-700', textColor: 'text-gray-600' },
      { range: '80-90', min: 80, max: 90, count: 0, color: 'bg-gray-800', textColor: 'text-gray-600' },
      { range: '90-100', min: 90, max: 100, count: 0, color: 'bg-gray-800', textColor: 'text-gray-600' },
      { range: '100+', min: 100, max: Infinity, count: 0, color: 'bg-gray-900', textColor: 'text-gray-600' }
    ];

    prices.data.forEach((p: any) => {
      const price = safeNumber(p.priceUsd, 0);
      const bin = bins.find(b => price >= b.min && price < b.max);
      if (bin) bin.count++;
    });

    return bins.filter(b => b.count > 0);
  }, [prices]);

  const priceDistributionData = {
    labels: priceDistribution.map(b => b.range),
    datasets: [{
      label: 'Hours',
      data: priceDistribution.map(b => b.count),
      backgroundColor: priceDistribution.map(b => {
        if (b.range === '0') return '#9ca3af';
        if (b.min < retailTariffValue && b.max <= retailTariffValue) return '#6b7280';
        if (b.min < retailTariffValue && b.max > retailTariffValue) return '#4b5563';
        return '#374151';
      })
    }]
  };

  const priceDistributionOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context: any) => `${context.parsed.y} hours (${((context.parsed.y / (prices?.data?.length || 1)) * 100).toFixed(1)}%)`
        }
      },
      annotation: {
        annotations: {
          retailLine: {
            type: 'line',
            yMin: 0,
            yMax: 'max',
            xMin: priceDistribution.findIndex(b => b.max > retailTariffValue) - 0.5,
            xMax: priceDistribution.findIndex(b => b.max > retailTariffValue) - 0.5,
            borderColor: '#a855f7',
            borderWidth: 2,
            borderDash: [5, 5],
            label: {
              content: `Retail: $${formatNumber(retailTariffValue, 0)}/MWh`,
              display: true,
              position: 'start',
              backgroundColor: '#a855f7',
              color: 'white',
              font: { size: 10 }
            }
          }
        }
      }
    },
    scales: {
      x: { title: { display: true, text: 'Price Range ($/MWh)' } },
      y: { title: { display: true, text: 'Number of Hours' } }
    }
  };

  const valueCurveChartData = {
    labels: analysis?.valueCaptureCurve?.map(v => v.hours) || [],
    datasets: [{
      label: 'Cumulative Savings',
      data: analysis?.valueCaptureCurve?.map(v => v.savings) || [],
      borderColor: '#a855f7',
      backgroundColor: 'rgba(168, 85, 247, 0.1)',
      tension: 0.4,
      fill: true
    }]
  };

  const valueCurveOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context: any) => [
            `Hours: ${context.parsed.x}`,
            `Savings: ${formatCurrency(context.parsed.y)}`,
            `Progress: ${formatNumber(analysis?.valueCaptureCurve?.[context.dataIndex]?.percentOfTotal || 0, 1)}%`
          ]
        }
      },
      annotation: {
        annotations: {
          targetHours: {
            type: 'line',
            xMin: analysis?.theoretical?.hoursNeeded || 0,
            xMax: analysis?.theoretical?.hoursNeeded || 0,
            borderColor: '#10b981',
            borderWidth: 2,
            borderDash: [5, 5],
            label: {
              content: `Target: ${formatNumber(analysis?.theoretical?.hoursNeeded || 0)} hrs`,
              display: true,
              position: 'start'
            }
          }
        }
      }
    },
    scales: {
      x: { title: { display: true, text: 'Hours Used (Cheapest First)' } },
      y: {
        title: { display: true, text: 'Cumulative Savings ($)' },
        ticks: {
          callback: (value: any) => formatCurrency(value)
        }
      }
    }
  };

  if (!prices?.data || !consumption?.data) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
        <div className="flex items-center gap-3 text-gray-500">
          <Info className="w-5 h-5" />
          <p className="text-sm">Price and consumption data required for theoretical maximum calculation</p>
        </div>
      </div>
    );
  }

  if (!analysis) return null;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
          <h3 className="text-base font-semibold text-black flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-gray-100 flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-brand-primary" />
            </div>
            Theoretical Maximum Savings Analysis
          </h3>
          <p className="text-sm text-gray-500 mt-1 ml-11">
            Value-ranked hour filling approach with statistical confidence
          </p>
          <p className="text-sm text-gray-600 mt-3 ml-11">
            We calculate the theoretical maximum by assuming perfect load shifting: all consumption moves to the cheapest hours of the year. This gives us the upper bound of possible savings.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <EnergySavingsMaximumCard analysis={analysis} configuration={configuration} />
        <PeakShavingMaximumCard analysis={analysis} exchangeRate={exchangeRate} />
      </div>

      <DataCoverageInfo prices={prices} consumption={consumption} />

      <ValueCaptureCurve
        analysis={analysis}
        valueCurveChartData={valueCurveChartData}
        valueCurveOptions={valueCurveOptions}
      />

      <PriceDistribution
        analysis={analysis}
        retailTariffValue={retailTariffValue}
        prices={prices}
        priceDistribution={priceDistribution}
        priceDistributionData={priceDistributionData}
        priceDistributionOptions={priceDistributionOptions}
      />
    </div>
  );
}